import { DirectoryOrganizations, Users } from '@platform/client';
import { OrganizationApplicationStatusEnum } from '@platform/client/schemas';
import { Alert } from '@platform/components';
import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'wouter';
import { ROUTES } from '../../routes/consts';
import MetaTags from '../../util/MetaTags';
import { DirectoryInnerSection } from '../ContentSection';
import { DirectoryOuterSection } from '../DirectoryContentGrid';
import { DirectoryLayout } from '../DirectoryLayout';
import { DirectoryPageContext } from '../DirectoryPageContext';
import IntroductionSentModal from '../IntroductionSentModal';
import { JoinAlert } from '../JoinAlert';
import { LogoTopPanel } from '../LogoTopPanel';
import { OrgDescriptions } from '../OrgDescriptions';
import { OrgInfoCard } from '../OrgInfoCard';
import { RepsList } from '../RepsList';
import SendIntroModal from '../SendIntroModal';
import { TwoColumnInnerContent } from '../TwoColumnInnerContent';
import { DeactivatedAlert } from './DeactivatedAlert';
import { OrgPillsPanel } from './OrgPillsPanel';
import { PartnerCard } from './PartnerCard';
import { ProjectCard } from './ProjectCard';

/** Organization directory page */
export const OrganizationProfile = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const { isSuccess: isSuccess, data: org } = DirectoryOrganizations.useGetOrganizationById(
    parseInt(orgId),
  );
  const { data: currentUser } = Users.useGetCurrentUser();
  const isOwnOrgPreview =
    currentUser?.accountType === 'organization' && currentUser.organization?.id === parseInt(orgId);
  const backLink =
    isOwnOrgPreview ?
      { label: 'Account Settings', href: '~/platform' + ROUTES.DASHBOARD + ROUTES.ACCOUNT }
    : {
        label: 'Browse organizations',
        href: '~/platform' + ROUTES.SEARCH_PROJECTS + '?view=organizations',
      };
  const [showRequestIntroModal, setShowRequestIntroModal] = useState(false);
  const [showRequestSentModal, setShowRequestSentModal] = useState(false);
  const leadRep = org?.reps.find((rep) => rep.id === org?.leadRepId);
  const orgIsUnavailable =
    isSuccess &&
    (!org.applicationStatus ||
      org.applicationStatus === OrganizationApplicationStatusEnum.deactivated ||
      org.applicationStatus === OrganizationApplicationStatusEnum.rejected);

  if (!isSuccess) {
    return (
      <DirectoryLayout backLink={backLink}>
        <LogoTopPanel avatarFallback={{ fallbackIcon: 'building-04' }} paddedAvatar>
          <Skeleton width={600} />
          <Skeleton width={300} />
        </LogoTopPanel>
        <DirectoryOuterSection noTopMargin>
          <DirectoryInnerSection title='About'>
            <TwoColumnInnerContent>
              <div className='flex flex-col gap-4'>
                <Skeleton width={600} />
                <Skeleton width={300} />
              </div>
            </TwoColumnInnerContent>
          </DirectoryInnerSection>
        </DirectoryOuterSection>
      </DirectoryLayout>
    );
  }

  return (
    <DirectoryLayout backLink={backLink}>
      <DirectoryPageContext.Provider
        value={{
          targetUserId: org.leadRepId,
          sendingFromRole: 'PROFESSIONAL',
          queryKey: DirectoryOrganizations.getGetOrganizationByIdQueryKey(parseInt(orgId)),
        }}
      >
        <MetaTags
          title={`${org.orgName} | MovingWorlds.org`}
          description={`${org.orgName} | MovingWorlds.org`}
        />
        <LogoTopPanel
          avatarUrl={org.avatarUrl ?? undefined}
          avatarFallback={{ fallbackIcon: 'building-06' }}
          paddedAvatar
        >
          <div className='text-2xl font-semibold text-gray-700 mb-1'>{org.orgName}</div>
          {org.locationCountry.length > 0 && (
            <div className='text-base text-gray-500 sm:mb-8'>
              {org.locationCity.length === 0 ?
                org.locationCountry
              : `${org.locationCity}, ${org.locationCountry}`}
            </div>
          )}
        </LogoTopPanel>
        <DirectoryOuterSection noTopMargin>
          <DirectoryInnerSection title='About'>
            <TwoColumnInnerContent>
              <div className='flex flex-col gap-4'>
                <OrgDescriptions orgData={org} maxLength={850} />
              </div>
              <OrgPillsPanel
                sustainabilityGoals={org.sustainabilityGoals}
                industries={org.industries}
              />
            </TwoColumnInnerContent>
            <OrgInfoCard isLoaded={isSuccess} orgData={org} />
          </DirectoryInnerSection>
          <DirectoryInnerSection title='Projects'>
            {org.projects.length > 0 &&
              org.applicationStatus !== OrganizationApplicationStatusEnum.deactivated && (
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  {(org.projects || []).map((project) => (
                    <ProjectCard
                      key={project.id}
                      projectName={project.name}
                      projectType={project.projectType}
                      tags={project.expertises}
                      projectId={project.id}
                    />
                  ))}
                </div>
              )}
            {(org.canProposeCustomProject || org.projects.length == 0) && !orgIsUnavailable && (
              <Alert
                variant='info'
                primaryCTA={{
                  text: 'Propose custom project',
                  callback: () => setShowRequestIntroModal(true),
                  dismissOnSuccess: false,
                }}
                overrideIcon='message-chat-circle'
                overrideIconColor='text-gray-600'
              >
                {org.canProposeCustomProject && org.projects.length > 0 && (
                  <>
                    Not finding the right fit? The fastest way to get involved is by introducing
                    yourself on an existing project. But if you have a unique idea or skillset you’d
                    like to bring, you can co-create a custom project directly with this
                    organization.
                  </>
                )}
                {org.projects.length == 0 && (
                  <>
                    This organization hasn't published projects at the moment — but if you'd like to
                    support their cause, you can reach out directly. Share how you think you could
                    help with their needs, and co-create a project together.
                  </>
                )}
              </Alert>
            )}
            {org.canViewJoinAlert && <JoinAlert />}
            {!org.canViewJoinAlert && orgIsUnavailable && <DeactivatedAlert />}
          </DirectoryInnerSection>
          <DirectoryInnerSection title='Our team'>
            <RepsList reps={org.reps} />
          </DirectoryInnerSection>
          {org.partners.length > 0 && (
            <DirectoryInnerSection
              title='Partners'
              tooltip='Partners shown here collaborate with this social enterprise and are also part of the MovingWorlds network. They include funders, accelerators, and capacity-building organizations that support enterprises tackling global challenges.'
            >
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-8'>
                {org.partners.map((partner) => (
                  <PartnerCard key={partner.name} logoUrl={partner.avatarUrl} name={partner.name} />
                ))}
              </div>
            </DirectoryInnerSection>
          )}
        </DirectoryOuterSection>
        {showRequestIntroModal && (
          <SendIntroModal
            isOpen={showRequestIntroModal}
            setIsOpen={setShowRequestIntroModal}
            onMessageSent={() => {
              setShowRequestIntroModal(false);
              setShowRequestSentModal(true);
            }}
          />
        )}
        {showRequestSentModal && (
          <IntroductionSentModal
            recipientName={leadRep?.name ?? org.orgName}
            isOpen={showRequestSentModal}
            setIsOpen={setShowRequestSentModal}
          />
        )}
      </DirectoryPageContext.Provider>
    </DirectoryLayout>
  );
};
